import { TextLink } from '@energiebespaarders/symbols';
import { Color, FontSize, Right } from '@energiebespaarders/symbols/helpers';
import { useCallback } from 'react';
import type { AnswerType, OmniformQuestion, QuestionAnswerType } from '~/hooks/omniform/types';
import type { WithFormState } from '~/hooks/omniform/useOmniform';
import useEventTracker from '~/hooks/useEventTracker';
import { QKeyGeneral } from '../CustomerIntake/types';
import type { IntakeTextVariables, QKeyCustomerIntake } from '../CustomerIntake/types';

const questionWithHelpers = [
  QKeyGeneral.HouseGasConsumption,
  QKeyGeneral.HouseElectricityConsumption,
  QKeyGeneral.HouseGasConsumptionMonthlyCost,
  QKeyGeneral.HouseElectricityConsumptionMonthlyCost,
  QKeyGeneral.HouseElectricityProduction,
] as string[];

interface InputHelperProps {
  question: WithFormState<OmniformQuestion>;
  onAnswer: (
    questionKey: string,
    answerType: QuestionAnswerType.Number,
    answer: AnswerType<QuestionAnswerType.Number>,
    skipNetwork?: boolean,
  ) => void;
  textVariables: IntakeTextVariables;
}

const variableMapping: Partial<Record<QKeyCustomerIntake, keyof IntakeTextVariables>> = {
  [QKeyGeneral.HouseGasConsumption]: 'gasConsumption',
  [QKeyGeneral.HouseElectricityConsumption]: 'electricityConsumption',
  [QKeyGeneral.HouseGasConsumptionMonthlyCost]: 'gasConsumptionMonthlyCost',
  [QKeyGeneral.HouseElectricityConsumptionMonthlyCost]: 'electricityConsumptionMonthlyCost',
  [QKeyGeneral.HouseElectricityProduction]: 'electricityProduction',
};

const InputHelper: React.FC<InputHelperProps> = ({ question, onAnswer, textVariables }) => {
  const trackEvent = useEventTracker();

  const helperId = `${question.key}-helper`;

  const relevantTextVariable = variableMapping[question.key as QKeyCustomerIntake] ?? undefined;
  const variable = relevantTextVariable ? textVariables[relevantTextVariable] : undefined;

  const handleClick = useCallback(() => {
    if (!variable) return;
    onAnswer(question.key, question.answerType as QuestionAnswerType.Number, variable);
    trackEvent('click_input_helper', { question: question.key });
  }, [onAnswer, question.answerType, question.key, trackEvent, variable]);

  if (!questionWithHelpers.includes(question.key)) return <></>;

  return (
    <FontSize size={7}>
      <Right block>
        <Color c="grayDarker">
          <TextLink onClick={handleClick} id={helperId}>
            <u>Inschatting overnemen</u>
          </TextLink>
        </Color>
      </Right>
    </FontSize>
  );
};

export default InputHelper;
